<template>
  <div class="type-certificate-page">
    <!-- ===================== Hero Header ===================== -->
    <div class="cert-hero">
      <div class="cert-hero__blob cert-hero__blob--one" />
      <div class="cert-hero__blob cert-hero__blob--two" />

      <div class="cert-hero__content">
        <div class="cert-hero__icon">
          <feather-icon
            icon="ShieldIcon"
            size="30"
          />
        </div>
        <div>
          <h2 class="cert-hero__title">
            Certificate Types
          </h2>
          <p class="cert-hero__subtitle">
            Manage regulatory certificate types, their linked certificates and issuing sources in one place.
          </p>
        </div>
      </div>

      <button
        type="button"
        class="cert-add-btn cert-add-btn--hero"
        @click="goToAdd"
      >
        <span class="cert-add-btn__icon">
          <feather-icon
            icon="PlusIcon"
            size="16"
          />
        </span>
        <span class="text-nowrap">{{ addType }}</span>
      </button>
    </div>

    <!-- ===================== Stats Header ===================== -->
    <b-row class="match-height mb-1">
      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <b-card
          no-body
          class="stat-card stat-card--primary"
        >
          <b-card-body class="d-flex justify-content-between align-items-center">
            <div>
              <h2 class="font-weight-bolder mb-0 text-white">
                {{ stats.totalTypes }}
              </h2>
              <span class="text-white-75">Certificate Types</span>
            </div>
            <b-avatar
              size="52"
              class="stat-avatar"
            >
              <feather-icon
                icon="LayersIcon"
                size="26"
              />
            </b-avatar>
          </b-card-body>
          <feather-icon
            icon="LayersIcon"
            size="90"
            class="stat-card__ghost-icon"
          />
        </b-card>
      </b-col>

      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <b-card
          no-body
          class="stat-card stat-card--success"
        >
          <b-card-body class="d-flex justify-content-between align-items-center">
            <div>
              <h2 class="font-weight-bolder mb-0 text-white">
                {{ stats.pageTypes }}
              </h2>
              <span class="text-white-75">On This Page</span>
            </div>
            <b-avatar
              size="52"
              class="stat-avatar"
            >
              <feather-icon
                icon="GridIcon"
                size="26"
              />
            </b-avatar>
          </b-card-body>
          <feather-icon
            icon="GridIcon"
            size="90"
            class="stat-card__ghost-icon"
          />
        </b-card>
      </b-col>

      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <b-card
          no-body
          class="stat-card stat-card--info"
        >
          <b-card-body class="d-flex justify-content-between align-items-center">
            <div>
              <h2 class="font-weight-bolder mb-0 text-white">
                {{ stats.totalCertificates }}
              </h2>
              <span class="text-white-75">Linked Certificates</span>
            </div>
            <b-avatar
              size="52"
              class="stat-avatar"
            >
              <feather-icon
                icon="AwardIcon"
                size="26"
              />
            </b-avatar>
          </b-card-body>
          <feather-icon
            icon="AwardIcon"
            size="90"
            class="stat-card__ghost-icon"
          />
        </b-card>
      </b-col>

      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <b-card
          no-body
          class="stat-card stat-card--warning"
        >
          <b-card-body class="d-flex justify-content-between align-items-center">
            <div>
              <h2 class="font-weight-bolder mb-0 text-white">
                {{ stats.totalSources }}
              </h2>
              <span class="text-white-75">Active Sources</span>
            </div>
            <b-avatar
              size="52"
              class="stat-avatar"
            >
              <feather-icon
                icon="Share2Icon"
                size="26"
              />
            </b-avatar>
          </b-card-body>
          <feather-icon
            icon="Share2Icon"
            size="90"
            class="stat-card__ghost-icon"
          />
        </b-card>
      </b-col>
    </b-row>

    <!-- ===================== Toolbar ===================== -->
    <b-card
      no-body
      class="cert-toolbar-card mb-1"
    >
      <div class="cert-toolbar">
        <div class="cert-toolbar__title">
          <b-avatar
            rounded
            variant="light-primary"
            size="40"
            class="mr-1"
          >
            <feather-icon
              icon="ListIcon"
              size="20"
            />
          </b-avatar>
          <div>
            <h4 class="mb-0 font-weight-bolder">
              Certificate Types
            </h4>
            <small class="text-muted">Manage certificate types and their sources</small>
          </div>
        </div>

        <div class="cert-toolbar__actions">
          <b-input-group class="cert-search">
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input
              v-model="searchQuery"
              placeholder="Search certificate types... (press Enter)"
              autocomplete="off"
              @keyup.enter="searchNow"
            />
            <b-input-group-append
              v-if="searchQuery"
              is-text
              class="cert-search__clear"
              @click="clearSearch"
            >
              <feather-icon icon="XIcon" />
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>
    </b-card>

    <!-- ===================== Loading ===================== -->
    <b-row v-if="dataLoad">
      <b-col
        v-for="n in 6"
        :key="n"
        cols="12"
        md="6"
        xl="4"
      >
        <div class="type-skel-card">
          <div class="type-skel-accent" />
          <div class="type-skel-head">
            <div class="type-skel-avatar" />
            <div class="type-skel-lines">
              <div class="type-skel-line type-skel-line--short" />
              <div class="type-skel-line type-skel-line--long" />
            </div>
          </div>
          <div class="type-skel-body">
            <div class="type-skel-chip" />
            <div class="type-skel-chip" />
            <div class="type-skel-chip" />
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- ===================== Empty state ===================== -->
    <b-card
      v-else-if="!rows.length"
      class="text-center py-5"
    >
      <feather-icon
        icon="InboxIcon"
        size="48"
        class="text-muted mb-1"
      />
      <h4>No certificate types found</h4>
      <p class="text-muted mb-1">
        {{ searchQuery ? 'Try adjusting your search.' : 'Start by adding your first certificate type.' }}
      </p>
      <button
        v-if="!searchQuery"
        type="button"
        class="cert-add-btn mx-auto"
        @click="goToAdd"
      >
        <span class="cert-add-btn__icon">
          <feather-icon
            icon="PlusIcon"
            size="16"
          />
        </span>
        <span class="text-nowrap">{{ addType }}</span>
      </button>
    </b-card>

    <!-- ===================== Type cards grid ===================== -->
    <b-row
      v-else
      class="types-grid"
    >
      <b-col
        v-for="row in rows"
        :key="row.id"
        cols="12"
        md="6"
        xl="4"
      >
        <div class="type-card">
          <div
            class="type-accent"
            :style="{ background: getTypeGradient(row) }"
          />

          <!-- Header -->
          <div class="type-card-head">
            <div
              class="type-avatar"
              :style="{ background: getTypeGradient(row) }"
            >
              <feather-icon
                :icon="getTypeIcon(row)"
                size="22"
              />
            </div>
            <div class="type-head-text">
              <h4
                class="type-name"
                :title="row.name"
              >
                {{ row.name }}
              </h4>
              <span class="type-id">#{{ row.id }}</span>
            </div>
          </div>

          <div class="type-card-body">
            <!-- Source -->
            <div class="type-section">
              <div class="section-label">
                <feather-icon
                  icon="Share2Icon"
                  size="14"
                  class="mr-25"
                />
                <span>Source</span>
              </div>
              <div
                v-if="row.schema_owner && row.schema_owner.length"
                class="source-chips"
              >
                <span
                  v-for="(src, i) in row.schema_owner"
                  :key="i"
                  class="source-chip"
                  :style="{ background: getSourceMeta(src).soft, color: getSourceMeta(src).color }"
                >
                  <feather-icon
                    :icon="getSourceMeta(src).icon"
                    size="12"
                    class="mr-25"
                  />
                  {{ src }}
                </span>
              </div>
              <span
                v-else
                class="empty-hint"
              >No source</span>
            </div>

            <!-- Certificates -->
            <div class="type-section">
              <div class="section-label">
                <feather-icon
                  icon="AwardIcon"
                  size="14"
                  class="mr-25"
                />
                <span>Certificates</span>
                <b-badge
                  pill
                  variant="light-primary"
                  class="ml-50"
                >
                  {{ (row.certificates || []).length }}
                </b-badge>
              </div>

              <div
                v-if="row.certificates && row.certificates.length"
                class="cert-list"
              >
                <div
                  v-for="(item, index) in row.certificates"
                  :key="item.id"
                  class="cert-list-item"
                >
                  <span class="cert-list-item__num">{{ index + 1 }}</span>
                  <div class="cert-list-item__text">
                    <span class="cert-list-item__en">{{ item.name.en || item.name.ar || 'Untitled certificate' }}</span>
                    <span
                      v-if="item.name.ar && item.name.en && item.name.ar !== item.name.en"
                      class="cert-list-item__ar"
                      dir="rtl"
                    >{{ item.name.ar }}</span>
                  </div>
                </div>
              </div>
              <div
                v-else
                class="no-certs"
              >
                <feather-icon
                  icon="FileMinusIcon"
                  size="14"
                  class="mr-25"
                />
                No certificates in this type
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="type-card-footer">
            <a
              :href="getEditLink(row)"
              target="_blank"
              rel="noopener"
              class="footer-btn footer-btn--edit"
            >
              <feather-icon
                icon="EditIcon"
                size="14"
                class="mr-25"
              />
              Edit
            </a>
            <span
              class="footer-btn footer-btn--delete"
              @click="deleteRow(row.id)"
            >
              <feather-icon
                icon="Trash2Icon"
                size="14"
                class="mr-25"
              />
              Delete
            </span>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- ===================== Pagination ===================== -->
    <div
      v-if="!dataLoad && rows.length"
      class="mt-1"
    >
      <Pagination
        :page="page"
        :per-page="perPage"
        :total-rows="totalRows"
        @get-all-data="getAllData"
      />
    </div>
  </div>
</template>

<script>
/* eslint-disable camelcase */
import axios from 'axios'
import {
  BRow, BCol, BCard, BCardBody, BAvatar, BBadge,
  BInputGroup, BInputGroupPrepend, BInputGroupAppend, BFormInput,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'
import Pagination from '@/components/general-table/specific-utils/Pagination.vue'

const TYPE_ICON_RULES = [
  { keys: ['water'], icon: 'DropletIcon' },
  { keys: ['batter'], icon: 'BatteryChargingIcon' },
  { keys: ['machinery'], icon: 'ToolIcon' },
  { keys: ['pressure', 'vessel'], icon: 'WindIcon' },
  { keys: ['oil', 'lub'], icon: 'ThermometerIcon' },
  { keys: ['paint'], icon: 'PenToolIcon' },
  { keys: ['ornament'], icon: 'StarIcon' },
  { keys: ['paper', 'cardboard'], icon: 'FileIcon' },
  { keys: ['detergent'], icon: 'CloudIcon' },
  { keys: ['packaging'], icon: 'PackageIcon' },
  { keys: ['adhesive', 'tape', 'glue'], icon: 'LinkIcon' },
  { keys: ['textile'], icon: 'ScissorsIcon' },
  { keys: ['child', 'restraint', 'stroller'], icon: 'UsersIcon' },
  { keys: ['household', 'appliance', 'electric', 'voltage'], icon: 'ZapIcon' },
  { keys: ['door', 'window'], icon: 'GridIcon' },
  { keys: ['construction', 'cladding', 'ceramic', 'metal', 'brick', 'pipe', 'tile'], icon: 'HomeIcon' },
]

const TYPE_ICON_FALLBACK = ['FileTextIcon', 'BoxIcon', 'ClipboardIcon', 'ArchiveIcon']

const TYPE_GRADIENTS = [
  'linear-gradient(135deg, #7367f0, #9e95f5)',
  'linear-gradient(135deg, #28c76f, #48da89)',
  'linear-gradient(135deg, #00cfe8, #1ce7ff)',
  'linear-gradient(135deg, #ff9f43, #ffbc7d)',
  'linear-gradient(135deg, #ea5455, #f97066)',
  'linear-gradient(135deg, #818cf8, #4f46e5)',
  'linear-gradient(135deg, #22d3ee, #0891b2)',
  'linear-gradient(135deg, #f6d365, #fda085)',
]

const SOURCE_META = {
  SLS: { color: '#00cfe8', soft: 'rgba(0, 207, 232, 0.12)', icon: 'ShieldIcon' },
  Saber: { color: '#28c76f', soft: 'rgba(40, 199, 111, 0.12)', icon: 'CheckCircleIcon' },
}
const DEFAULT_SOURCE_META = { color: '#7367f0', soft: 'rgba(115, 103, 240, 0.12)', icon: 'TagIcon' }

export default {
  components: {
    Pagination,
    BRow,
    BCol,
    BCard,
    BCardBody,
    BAvatar,
    BBadge,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BFormInput,
  },
  data() {
    return {
      addType: 'Add certificate type',
      addComponentName: 'add-type-certificate',
      editComponent: 'edit-type-certificate',
      rows: [],
      totalRows: 0,
      page: 1,
      perPage: 20,
      dataLoad: false,
      searchQuery: '',
      stats: {
        totalTypes: 0,
        pageTypes: 0,
        totalCertificates: 0,
        totalSources: 0,
      },
    }
  },
  mounted() {
    this.getAllData(this.page)
  },
  methods: {
    async getAllData(page = 1) {
      this.page = page
      this.dataLoad = true
      try {
        const params = {
          skip: (page - 1) * this.perPage,
          ...(this.searchQuery ? { search: this.searchQuery } : {}),
        }

        const response = await axios.get('certificate-types', { params })

        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          const firstKey = Object.keys(responseData?.data)[0]
          this.rows = responseData?.data[firstKey] || []
          this.totalRows = responseData.data.count || this.rows.length
          this.computeStats()
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed to load certificate types',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.dataLoad = false
      }
    },

    searchNow() {
      this.getAllData(1)
    },

    clearSearch() {
      this.searchQuery = ''
      this.getAllData(1)
    },

    computeStats() {
      const sources = new Set()
      let certificatesCount = 0

      this.rows.forEach(item => {
        if (Array.isArray(item.certificates)) {
          certificatesCount += item.certificates.length
        }
        if (Array.isArray(item.schema_owner)) {
          item.schema_owner.forEach(source => sources.add(source))
        }
      })

      this.stats = {
        totalTypes: this.totalRows,
        pageTypes: this.rows.length,
        totalCertificates: certificatesCount,
        totalSources: sources.size,
      }
    },

    goToAdd() {
      this.$router.push({ name: this.addComponentName })
    },

    getEditLink(row) {
      return this.$router.resolve({
        name: this.editComponent,
        params: { id: row.id, name: row.name },
      }).href
    },

    deleteRow(id) {
      this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
      }).then(result => {
        if (result.isConfirmed) {
          axios
            .delete(`certificate-types/${id}`)
            .then(() => {
              this.$swal('Deleted!', 'Deleted Successfully.', 'success')
              this.getAllData(this.page)
            })
            .catch(() => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Failed to delete',
                  icon: 'AlertTriangleIcon',
                  variant: 'danger',
                },
              })
            })
        }
      })
    },

    getTypeIcon(row) {
      const name = (row.name || '').toLowerCase()
      const rule = TYPE_ICON_RULES.find(({ keys }) => keys.some(key => name.includes(key)))

      if (rule) return rule.icon

      return TYPE_ICON_FALLBACK[row.id % TYPE_ICON_FALLBACK.length]
    },

    getTypeGradient(row) {
      const key = `${row.id}`
      let hash = 0
      for (let i = 0; i < key.length; i += 1) {
        hash = (hash * 31 + key.charCodeAt(i)) % 1000000007
      }
      return TYPE_GRADIENTS[Math.abs(hash) % TYPE_GRADIENTS.length]
    },

    getSourceMeta(source) {
      return SOURCE_META[source] || DEFAULT_SOURCE_META
    },
  },
}
</script>

<style lang="scss" scoped src="./TypeCertificate.scss"></style>
